import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  cond,
  equals,
  has,
  map,
  prop,
  reduce,
  replace,
  test,
} from 'ramda';
import { withStyles } from '@material-ui/core/styles';
import Divider from '@material-ui/core/Divider';
import CopyIcon from '@material-ui/icons/FileCopyOutlined';
import RevealIcon from '@material-ui/icons/VisibilityOffOutlined';
import Typography from '@material-ui/core/Typography';
import { FormField, TextField } from '@iris-platform/iris-react-ui';
import { withApollo } from 'react-apollo';
import {
  stylesToPropType,
  copyToClipboard,
  hideAfterAnimate,
  capitalize,
} from '../../utils';
import {
  GET_CLIENT_ID,
  GET_CLIENT_SECRET,
  GET_PRIVATE_KEY,
} from './queries';
import applicationPropType from './applicationPropType';

const REVEAL_ANIMATION_DURATION_MS = 500;
const SECRET_TTL_MS = 60000;

const copyButtonStyles = () => ({
  root: {
    cursor: 'pointer',
  },
  animateClick: {
    animation: 'bounce 0.35s ease-out',
  },
  '@keyframes bounce': {
    '0%': {
      transform: 'scale(1, 1)',
    },
    '25%': {
      transform: 'scale(1.14, 1.14)',
    },
    '50%': {
      transform: 'scale(1.2, 1.2)',
    },
    '75%': {
      transform: 'scale(1.07, 1.07)',
    },
    '100%': {
      transform: 'scale(1, 1)',
    },
  },
});

const UnstyledCopyButton = ({
  className,
  classes,
  value,
}) => (
  <CopyIcon
    className={`${className} ${classes.root}`}
    onClick={(e) => {
      let icon = e.target;

      while (icon.tagName.toLowerCase() !== 'svg') icon = icon.parentElement;

      window.requestAnimationFrame(() => {
        icon.classList.remove(classes.animateClick);

        setImmediate(() => {
          window.requestAnimationFrame(() => {
            icon.classList.add(classes.animateClick);
          });
        });
      });


      copyToClipboard(value);
    }}
  />
);

UnstyledCopyButton.propTypes = {
  className: PropTypes.string,
  classes: stylesToPropType(copyButtonStyles()).isRequired,
  value: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.number,
    PropTypes.string,
  ]).isRequired,
};

UnstyledCopyButton.defaultProps = {
  className: '',
};

const CopyButton = withStyles(copyButtonStyles)(UnstyledCopyButton);


const revealButtonStyles = () => ({
  root: {
    cursor: 'pointer',
  },
  animateClick: {
    animation: `flush ${REVEAL_ANIMATION_DURATION_MS / 1000}s ease-out`,
  },
  '@keyframes flush': {
    from: {
      transform: 'rotate(0deg) scale(1, 1)',
      opacity: 1,
    },
    to: {
      transform: 'rotate(360deg) scale(0, 0)',
      opacity: 0,
    },
  },
});

const UnstyledRevealButton = ({
  className,
  classes,
  onClick,
}) => (
  <RevealIcon
    className={`${className} ${classes.root}`}
    onClick={(e) => {
      let icon = e.target;

      while (icon.tagName.toLowerCase() !== 'svg') icon = icon.parentElement;

      window.requestAnimationFrame(() => {
        icon.classList.remove(classes.animateClick);
        hideAfterAnimate(icon);

        setImmediate(() => {
          window.requestAnimationFrame(() => {
            icon.classList.add(classes.animateClick);
          });
        });
      });


      onClick();
    }}
  />
);

UnstyledRevealButton.propTypes = {
  className: PropTypes.string,
  classes: stylesToPropType(revealButtonStyles()).isRequired,
  onClick: PropTypes.func,
};

UnstyledRevealButton.defaultProps = {
  className: '',
  onClick: () => {},
};

const RevealButton = withStyles(revealButtonStyles)(UnstyledRevealButton);

const isRevealed = test(/[^*\n]/);

const getQueryForField = cond([
  [equals('clientID'), () => GET_CLIENT_ID],
  [equals('clientSecret'), () => GET_CLIENT_SECRET],
  [equals('privateKey'), () => GET_PRIVATE_KEY],
]);

const redact = replace(/./g, '*');

const styles = () => ({
  root: {
    display: 'flex',
    flexFlow: 'column nowrap',
  },
  disabledField: {
    cursor: 'default',
    '& *': {
      color: '#606060',
    },
  },
  endAdornment: {
    marginRight: '0.5em',
  },
  expiring: {
    position: 'relative',
    zIndex: 0,
    '& svg.timer': {
      zIndex: 0,
      position: 'absolute',
      top: '-0.63em',
      left: '-0.63em',
      height: '2.9em',
      width: '2.9em',
      transform: 'rotate(-90deg)',
      '& circle': {
        stroke: '#d2d2d28f',
        strokeWidth: '40',
        fill: 'none',
        strokeDasharray: '126 252',
        animation: `wipe ${SECRET_TTL_MS / 1000}s linear`,
      },
    },
    '& svg:not(.timer)': {
      zIndex: 1,
      position: 'sticky',
    },
  },
  divider: {
    margin: '2em 0',
  },
  textField: {
    height: 'auto',
    paddingLeft: '0.5em',
    '&::before, &:hover::before': {
      display: 'none !important',
    },
  },
  '@keyframes wipe': {
    '0%': {
      strokeDashoffset: 0,
    },
    '100%': {
      strokeDashoffset: -126,
    },
  },
});

class UnstyledCredentialsTab extends Component {
  constructor(props) {
    super(props);

    const { application } = props;
    this.state = {
      ...application,
      authString: `${application.clientID}*${application.clientSecret}`,
    };
  }

  reveal = async (...fields) => {
    const start = new Date();
    const { client } = this.props;
    const { applicationID } = this.state;

    const queries = map(
      async (field) => {
        const query = getQueryForField(field);
        const { data } = await client.query({ query, variables: { applicationID } });
        const value = data[`application${capitalize(field)}`];

        return { [field]: value };
      },
      fields,
    );
    const results = await Promise.all(queries);
    const update = reduce((result, accum) => ({ ...accum, ...result }), {}, results);

    if (has('clientID', update) && has('clientSecret', update)) {
      update.authString = btoa(`${
        prop('clientID', update)
      }:${
        prop('clientSecret', update)
      }`);
    }

    const redaction = map(redact, update);

    const queryDuration = (new Date()) - start;
    const delay = Math.max(0, REVEAL_ANIMATION_DURATION_MS - queryDuration);
    setTimeout(() => this.setState(update), delay);
    setTimeout(() => this.setState(redaction), SECRET_TTL_MS);
  };

  render() {
    const { classes } = this.props;
    const {
      authString,
      federationType,
      clientID,
      clientSecret,
      publicKey,
      privateKey,
    } = this.state;

    return (
      <form className={classes.root}>
        <FormField
          label="Federation Type"
          inputClasses={{ input: classes.disabledField }}
          value={federationType}
          endAdornment={(
            <CopyButton
              className={classes.endAdornment}
              value={federationType}
            />
          )}
        />
        <FormField
          label="Client ID"
          inputClasses={{
            input: classes.disabledField,
          }}
          value={clientID}
          endAdornment={isRevealed(clientID) ? (
            <span className={classes.expiring}>
              <svg className="timer" viewBox="0 0 100 100">
                <circle cx="50" cy="50" r="20" />
              </svg>
              <CopyButton
                className={classes.endAdornment}
                value={clientID}
              />
            </span>
          ) : (
            <RevealButton
              className={classes.endAdornment}
              onClick={() => this.reveal('clientID')}
            />
          )}
        />
        <FormField
          label="Client Secret"
          inputClasses={{
            input: classes.disabledFieldng,
          }}
          value={clientSecret}
          endAdornment={isRevealed(clientSecret) ? (
            <span className={classes.expiring}>
              <svg className="timer" viewBox="0 0 100 100">
                <circle cx="50" cy="50" r="20" />
              </svg>
              <CopyButton
                className={classes.endAdornment}
                value={clientSecret}
              />
            </span>
          ) : (
            <RevealButton
              className={classes.endAdornment}
              onClick={() => this.reveal('clientSecret')}
            />
          )}
        />
        <FormField
          label="Auth String"
          inputClasses={{
            input: classes.disabledField,
          }}
          value={authString}
          endAdornment={isRevealed(authString) ? (
            <span className={classes.expiring}>
              <svg className="timer" viewBox="0 0 100 100">
                <circle cx="50" cy="50" r="20" />
              </svg>
              <CopyButton
                className={classes.endAdornment}
                value={authString}
              />
            </span>
          ) : (
            <RevealButton
              className={classes.endAdornment}
              onClick={() => this.reveal('clientID', 'clientSecret')}
            />
          )}
        />
        <Divider
          variant="middle"
          classes={{ root: classes.divider }}
        />
        <Typography
          component="h3"
          variant="subheading"
          gutterBottom
        >
          JWT Keys
        </Typography>
        <br />
        <TextField
          label="Public Key"
          value={publicKey}
          className={classes.textField}
          multiline
          disabled
          InputProps={{
            className: `${classes.disabledField} ${classes.textField}`,
            endAdornment: (
              <CopyButton
                className={classes.endAdornment}
                value={publicKey}
              />
            ),
          }}
        />
        <br />
        <TextField
          label="Private Key"
          value={privateKey}
          className={classes.textField}
          multiline
          disabled
          InputProps={{
            className: `${classes.disabledField} ${classes.textField}`,
            endAdornment: isRevealed(privateKey) ? (
              <span className={classes.expiring}>
                <svg className="timer" viewBox="0 0 100 100">
                  <circle cx="50" cy="50" r="20" />
                </svg>
                <CopyButton
                  className={classes.endAdornment}
                  value={privateKey}
                />
              </span>
            ) : (
              <RevealButton
                className={classes.endAdornment}
                onClick={() => this.reveal('privateKey')}
              />
            ),
          }}
        />
      </form>
    );
  }
}

UnstyledCredentialsTab.propTypes = {
  application: applicationPropType.isRequired,
  classes: stylesToPropType(styles()).isRequired,
  client: PropTypes.object.isRequired,
};

const CredentialsTab = withStyles(styles)(UnstyledCredentialsTab);

export default withApollo(CredentialsTab);
